import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLeft, HiCheck } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { getPasswordValidationErrorKey } from "../../../utils/passwordValidation";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./CreateUser.scss";

export const CreateUser: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['users', 'common']);
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    password: "",
    email: "",
    roleId: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [roles, setRoles] = useState([]);
  // Fetch roles list
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await adminApi.call('/role', 'GET');
        if (!response.success) {
          showErrorToast("Error: " + response.error);
          return;
        }

        setRoles(response.data?.items || []);
      } catch (error) {
        showErrorToast("Error: " + (error instanceof Error ? error.message : String(error)));
      }
    };

    fetchRoles();
  }, []);
  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;

    // Validation for name field (letters, spaces, and common name characters only)
    if (name === 'name') {
      const nameRegex = /^[a-zA-Z\u0600-\u06FF\s'-]*$/; // English, Arabic letters, spaces, apostrophes, hyphens
      if (!nameRegex.test(value)) {
        return; // Don't update if invalid characters
      }
    }

    // Validation for phone field (numbers only)
    if (name === 'phone') {
      const phoneRegex = /^[0-9]*$/;
      if (!phoneRegex.test(value)) {
        return; // Don't update if invalid characters
      }
    }

    setFormData((prevData) => ({ ...prevData, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = t('users:validation.nameRequired');
    } else if (formData.name.length < 2) {
      newErrors.name = t('users:validation.nameMinLength');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('users:validation.phoneRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('users:validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('users:validation.emailInvalid');
    }

    if (!formData.roleId) {
      newErrors.roleId = t('users:validation.roleRequired');
    }

    if (!formData.password.trim()) {
      newErrors.password = t('users:validation.passwordRequired');
    } else {
      const passwordValidationErrorKey = getPasswordValidationErrorKey(formData.password, formData.phone);
      if (passwordValidationErrorKey) {
        newErrors.password = t(`users:validation.${passwordValidationErrorKey}`);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createProfile = async () => {
    // Validate form before submission
    if (!validateForm()) {
      showErrorToast(t('users:validation.fillAllFields'));
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/user/create', 'POST', formData);

      if (!response.success) {
        showErrorToast(response.error || t('users:messages.createError'));
        return;
      }

      showSuccessToast(t('users:messages.createSuccess'));
      // Reset form
      setFormData({
        name: "",
        phone: "",
        password: "",
        email: "",
        roleId: "",
      });
    } catch (error: any) {
      showErrorToast(t('users:messages.createError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('users:breadcrumbs.users'), path: '/users' },
    { label: t('users:breadcrumbs.create') }
  ];

  return (
    <PageLayout
      title={t('users:createUser')}
      subtitle={t('users:subtitle')}
      breadcrumbs={breadcrumbs}
    >
      <div className="modern-form">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('users:fields.name')} <span className="required">*</span></label>
            <input
              type="text"
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('users:placeholders.name')}
              required
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('users:fields.email')} <span className="required">*</span></label>
            <input
              type="email"
              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('users:placeholders.email')}
              required
            />
            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('users:fields.phone')} <span className="required">*</span></label>
            <input
              type="text"
              className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder={t('users:placeholders.phone')}
              required
            />
            {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('users:fields.role')} <span className="required">*</span></label>
            <select
              className={`form-control ${errors.roleId ? 'is-invalid' : ''}`}
              name="roleId"
              value={formData.roleId || ""}
              onChange={handleInputChange}
              required
            >
              <option value="">{t('users:placeholders.role')}</option>
              {roles.map((item: any) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            {errors.roleId && <div className="invalid-feedback">{errors.roleId}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('users:fields.password')} <span className="required">*</span></label>
            <input
              type="password"
              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t('users:placeholders.password')}
              required
            />
            {errors.password && <div className="invalid-feedback">{errors.password}</div>}
          </div>
        </div>

        <div className="form-actions">
          <ActionButton
            variant="primary"
            icon={HiCheck}
            onClick={createProfile}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('users:buttons.submitting') : t('users:buttons.submit')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
